// Level-3 restyle (Sep 2026) — per-browser UI preferences: colour theme
// (light / dark / system) and table density (comfortable / compact).
// Both are applied as `data-*` attributes on <html> so the SCSS tokens
// (styles/_theme.scss) and the AntD ConfigProvider (index.js) can follow.
// Nothing here reaches the backend: these are viewer conveniences.

const THEME_KEY = 'ovo-theme-v1'; // 'light' | 'dark' | 'system'
const DENSITY_KEY = 'ovo-density-v1'; // 'comfortable' | 'compact'
export const THEME_EVENT = 'ovo-theme-change';

const safeGet = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};
const safeSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    /* private mode / blocked storage — keep the in-memory value only */
  }
};

// Default is LIGHT (dark is opt-in from the header toggle — operators must
// never be surprised by a dark planner because their OS is dark).
export const getThemeMode = () => {
  const v = safeGet(THEME_KEY);
  return v === 'dark' || v === 'light' || v === 'system' ? v : 'light';
};

const systemPrefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

// 'light' | 'dark' — what is actually painted right now.
export const resolveTheme = (mode = getThemeMode()) =>
  mode === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode;

export const applyTheme = (mode = getThemeMode()) => {
  const resolved = resolveTheme(mode);
  const root = document.documentElement;
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { mode, resolved } }));
  return resolved;
};

export const setThemeMode = (mode) => {
  safeSet(THEME_KEY, mode);
  return applyTheme(mode);
};

export const getDensity = () => (safeGet(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable');

export const applyDensity = (density = getDensity()) => {
  document.documentElement.dataset.density = density;
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { density } }));
  return density;
};

export const setDensity = (density) => {
  safeSet(DENSITY_KEY, density === 'compact' ? 'compact' : 'comfortable');
  return applyDensity(getDensity());
};

// Boot: paint the stored preference before React mounts (no flash), and
// follow the OS when the mode is 'system'.
export const initUiPrefs = () => {
  applyTheme();
  applyDensity();
  if (window.matchMedia) {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (getThemeMode() === 'system') applyTheme('system');
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }
};
